// The fair-weather sky (art/model-integration.md 9.4): low cumulus to dive
// through and a thin high deck above the zone ceiling, every puff a sprite
// cut from the Blender-baked puff atlas (art/clouds, 1g) instead of a stack
// of translucent boxes.
//
// How a puff is drawn: a camera-facing card that stays UPRIGHT in the world —
// the chase camera rolls a third of the way into a bank, and a card that
// rolled with it would tip every flat cloud base against the horizon. Seen
// from straight above or below, where "upright" stops meaning anything, the
// card eases onto the camera's own axes instead of spinning in place. The
// atlas carries each puff lit three ways (from the left in red, from above in
// green, from the right in blue); the shader blends them by where the key
// light sits in the card's frame, so a puff keeps its own baked
// self-shadowing whether the sun is behind you, over it, or setting beside
// it, and a cloud with the sun behind it gets a silver rim. The light is the
// sky's own — sun or moon, its colour and strength, the hemisphere fill — so
// noon cumulus is white, sunset cumulus burns, and night cumulus goes slate.
//
// Where the puffs are: exactly where the block decks put their blocks. The
// dice are the same hashes of the same map seed, so every client still sees
// one shared cloud field and a plane hiding in a cloud is hidden from all of
// them. Only the card's size, shape and tint are new dice.
//
// Flying through one: a card thins away as you reach it (a quad slicing past
// the nose reads as a sheet of paper, not weather), and inside a puff's
// volume a soft veil in the cloud's own light closes over the view — capped
// short of white-out, so the pilot keeps the aircraft they're flying.
//
// Cost: one draw a deck, as the boxes were. Fill is what a phone pays for
// clouds, so each card is trimmed to the rectangle its own puff covers (38 %
// of an atlas cell on average), and a card thinned to nothing as you reach it
// collapses before it's rasterized rather than filling the screen with
// fragments that all discard. The atlas is one 1024 px texture, fetched after
// boot and runtime-cached with the models; the sky stays clear until it lands.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";

// A literal URL so Vite content-hashes it into assets/models/ (build-models.mjs
// copies it there from art/clouds).
const ATLAS_URL = new URL("../models/cloud_puffs_light.png", import.meta.url);

// The atlas (model-integration.md 1g): a 4x4 grid of puffs, each drawn in a
// square frame with its flat base 23 % up from the frame's bottom edge.
export const PUFF_GRID = 4;
export const PUFF_BASE = 0.23;
// The part of its frame each puff's card draws, [x0, x1, y0, y1] in frame
// units (0..1, y up from the bottom edge): the puff's own coverage plus three
// texels, so nothing is spent on the transparent margin round it (a
// full-cell card fills 2.6 times the area). clouds.test.mjs holds every puff
// in the atlas inside its rectangle; a re-render that moves one fails there.
export const PUFF_RECTS = [
  [0.168, 0.9297, 0.2109, 0.7734],
  [0.2148, 0.8125, 0.2148, 0.7578],
  [0.2031, 0.7148, 0.2148, 0.7734],
  [0.2422, 0.7813, 0.2109, 0.8086],
  [0.2969, 0.7813, 0.2109, 0.7578],
  [0.2188, 0.8828, 0.2148, 0.7969],
  [0.2422, 0.8633, 0.2109, 0.7695],
  [0.1172, 0.8359, 0.2188, 0.8125],
  [0.0938, 0.8711, 0.2148, 0.7852],
  [0.1484, 0.9023, 0.2148, 0.793],
  [0.1563, 0.8828, 0.2109, 0.8125],
  [0.1641, 0.7305, 0.2109, 0.8125],
  [0.2734, 0.9023, 0.2109, 0.7578],
  [0.2383, 0.832, 0.2148, 0.793],
  [0.0977, 0.8945, 0.2109, 0.7344],
  [0.125, 0.9609, 0.2109, 0.8203],
];

// The two decks. The first block of fields are the block decks' dice, kept
// as they were so the clouds stand where they always stood: `count` cards
// in clusters of 1..maxPerPuff about a hashed centre, `scatter` across and
// ±18 m of height between the cards of one cloud. The rest is the card:
//   frameK  — frame size per metre of the old block's size, so a puff is
//             about as wide as the block it replaces (and rounder)
//   squash  — height scale of the card (the high deck's are flattened into
//             sheets you look up at)
//   opacity — the deck's strength (the storm front dims it; storm.js)
//   satMax  — how far a cloud's tint may lean off white: the old decks'
//             mother-of-pearl sheen, one gentle hue per cloud now, not one
//             per block
const DECKS = {
  // Low cumulus right in the fight: fat puffs to dive through and hide in.
  low: {
    seedOff: 0, count: 340, maxPerPuff: 6, yBase: 450, ySpread: 520,
    sMin: 26, sMax: 84, scatter: 130,
    frameK: 2.5, squash: 1, opacity: 0.95, satMax: 0.1,
  },
  // A high thin deck above the zone ceiling: wide, flat and faint.
  high: {
    seedOff: 9001, count: 130, maxPerPuff: 2, yBase: 1800, ySpread: 900,
    sMin: 90, sMax: 190, scatter: 260,
    frameK: 2.4, squash: 0.36, opacity: 0.55, satMax: 0.06,
  },
};

// Shading gains (ambient, sun, rim, fog thinning), tuned by eye against the
// Lambert world in every theater at noon, golden hour and night.
const SHADE = [0.6, 0.27, 0.6, 0.55];
// At golden hour the sky dims its sun for the ground, which is going into
// shade — but a cumulus a kilometre up still stands in the last direct light,
// and that is the whole sunset: the puffs glow while the fields go dusky.
// The key light on the cards swells by up to this much at the golden bell's
// peak (buildSky's duskK).
const DUSK_GLOW = 1.6;
// Tier 2 films the frame (ACES, gfx2.js), which rolls a sunlit white off to
// a light grey — right for a wing, flat for a cumulus against its pale
// scattered sky. Under tone mapping the cards are lit this much harder.
const TONE_LIFT = 1.3;
// A card thins over this span of distances, in frame sizes: gone at the
// first, whole at the second. Inside the first the veil has taken over.
const NEAR_FADE = [0.34, 0.75];
// The in-cloud veil: how thick it gets at a puff's core (1 = solid), and the
// puff volume it measures, as radii in frame sizes about the puff's middle.
const VEIL_MAX = 0.72;
// The light inside: a puff's mid-grey interior, lit about as a bulge is.
const VEIL_LIT = 0.7;
const VEIL_R = { across: 0.3, up: 0.2, mid: 0.3 };

/**
 * The deck's cards, pure dice from the map seed: one entry per card with the
 * puff's base centre (x, y, z), frame size, atlas cell, mirror (±1), height
 * squash, opacity and tint (r, g, b). `cloud` is the index of the cluster.
 */
export function cloudCards(seed, radius, deck) {
  const d = typeof deck === "string" ? DECKS[deck] : deck;
  const s0 = (seed + d.seedOff) >>> 0;
  const cards = [];
  const hue = new THREE.Color();
  let puff = 0;
  while (cards.length < d.count) {
    // The block decks' dice, unchanged: where the cloud is and how many
    // cards make it up.
    const px = (hash2(puff, 1, s0) - 0.5) * radius * 2.4;
    const pz = (hash2(puff, 2, s0) - 0.5) * radius * 2.4;
    const py = d.yBase + hash2(puff, 3, s0) * d.ySpread;
    const blocks = 1 + Math.floor(hash2(puff, 4, s0) * d.maxPerPuff);
    // One gentle hue per cloud, so its cards read as one body: white, leaned
    // a little way toward the hue.
    hue.setHSL(hash2(puff, 60, s0), 1, 0.5);
    const lean = hash2(puff, 70, s0) * d.satMax;
    for (let b = 0; b < blocks && cards.length < d.count; b++) {
      const s = d.sMin + hash2(puff, 40 + b, s0) * (d.sMax - d.sMin);
      const size = s * d.frameK;
      cards.push({
        cloud: puff,
        x: px + (hash2(puff, 10 + b, s0) - 0.5) * d.scatter,
        // The old block's centre, less half its height: the puff's flat base
        // sits where the block's floor was.
        y: py + (hash2(puff, 30 + b, s0) - 0.5) * 36 - s * 0.25 * d.squash,
        z: pz + (hash2(puff, 20 + b, s0) - 0.5) * d.scatter * 0.75,
        size,
        cell: Math.floor(hash2(puff, 90 + b, s0) * PUFF_GRID * PUFF_GRID),
        mirror: hash2(puff, 100 + b, s0) < 0.5 ? -1 : 1,
        squash: d.squash * (0.85 + 0.3 * hash2(puff, 110 + b, s0)),
        opacity: 0.8 + 0.2 * hash2(puff, 80 + b, s0),
        r: 1 + (hue.r - 1) * lean,
        g: 1 + (hue.g - 1) * lean,
        b: 1 + (hue.b - 1) * lean,
      });
    }
    puff++;
  }
  return cards;
}

// The middle of a card's puff, where its volume and its distance are taken.
export function puffMid(c, out) {
  out[0] = c.x;
  out[1] = c.y + c.size * c.squash * VEIL_R.mid;
  out[2] = c.z;
  return out;
}

/**
 * How deep `eye` sits inside the cards' puffs: 0 outside every one, rising
 * to 1 at a puff's core (overlapping puffs thicken together).
 */
export function cloudDepth(cards, ex, ey, ez) {
  let clear = 1;
  const m = [0, 0, 0];
  for (const c of cards) {
    const rx = c.size * VEIL_R.across;
    const ry = c.size * c.squash * VEIL_R.up;
    puffMid(c, m);
    const dy = (ey - m[1]) / ry;
    if (dy > 1 || dy < -1) continue;
    const dx = (ex - m[0]) / rx, dz = (ez - m[2]) / rx;
    const q = dx * dx + dy * dy + dz * dz;
    if (q < 1) clear *= Math.sqrt(q); // 1 - depth, depth = 1 - |q|
  }
  return 1 - clear;
}

/**
 * The camera's right axis, as the cards' fallback for straight-up and
 * straight-down views (the shader keeps them upright everywhere else).
 */
export function cameraRight(camera, out = new THREE.Vector3()) {
  camera.updateMatrixWorld();
  return out.setFromMatrixColumn(camera.matrixWorld, 0).normalize();
}

const VERT = /* glsl */ `
attribute vec2 corner;  // the card's corner, 0..1 across its puff's rectangle
attribute vec4 iBase;   // xyz: the puff's base centre (local to the deck); w: frame size (m)
attribute vec4 iLook;   // x: atlas cell; y: mirror (+1 / -1); z: height squash; w: opacity
attribute vec4 iRect;   // the puff's rectangle in its frame: x0, x1, y0, y1
attribute vec3 iTint;
#ifdef CARD_GLOW
attribute float iGlow;  // lightning in this card's belly, 0..1 (storm.js)
varying float vGlow;
#endif
uniform vec3 uCamRight;
uniform vec3 uSunDir;   // toward the key light (sun or moon), world space
uniform vec2 uNearFade;
varying vec2 vUv;
varying vec3 vSun;      // the key light in the card's frame: x right, y up, z toward the eye
varying vec3 vTint;
varying float vAlpha;
#include <fog_pars_vertex>

void main() {
  float size = iBase.w;
  vec3 base = (modelMatrix * vec4(iBase.xyz, 1.0)).xyz;
  vec3 mid = base + vec3(0.0, size * iLook.z * ${VEIL_R.mid.toFixed(3)}, 0.0);
  vec3 toEye = cameraPosition - mid;
  float dist = length(toEye);
  vAlpha = iLook.w * smoothstep(uNearFade.x * size, uNearFade.y * size, dist);
  if (vAlpha < 0.004) {
    // Thinned to nothing (the eye is in it): park the card outside the clip
    // volume, so a quad that would cover the screen costs no fragments.
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec3 n = toEye / max(dist, 1e-3);
  // Upright: the card's right is level. Looking straight up or down at it,
  // "level" has no direction, so hand over to the camera's right.
  vec3 level = cross(vec3(0.0, 1.0, 0.0), n);
  float h = length(level);
  vec3 right = normalize(mix(uCamRight, level / max(h, 1e-4), smoothstep(0.08, 0.3, h)));
  vec3 up = normalize(cross(n, right));
  // Where in its frame this corner is: the frame's centre sits over the
  // puff's base centre, the base PUFF_BASE up from the frame's bottom edge.
  float fx = mix(iRect.x, iRect.y, corner.x);
  float fy = mix(iRect.z, iRect.w, corner.y);
  vec3 wp = base + right * ((fx - 0.5) * size) + up * ((fy - ${PUFF_BASE.toFixed(3)}) * size * iLook.z);

  // A mirrored puff flips its picture inside its rectangle (the card itself
  // keeps its winding, so it stays front-facing), and its left-lit side now
  // faces the other way.
  float mirror = iLook.y;
  float fu = mirror < 0.0 ? iRect.x + iRect.y - fx : fx;
  float cell = iLook.x;
  float col = mod(cell, ${PUFF_GRID.toFixed(1)});
  float row = floor(cell / ${PUFF_GRID.toFixed(1)});
  vUv = vec2(col + fu, ${(PUFF_GRID - 1).toFixed(1)} - row + fy) / ${PUFF_GRID.toFixed(1)};
  vSun = vec3(dot(uSunDir, right) * mirror, dot(uSunDir, up), dot(uSunDir, n));
  vTint = iTint;
  #ifdef CARD_GLOW
  vGlow = iGlow;
  #endif

  vec4 mvPosition = viewMatrix * vec4(wp, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
uniform vec3 uSunCol;   // the key light's colour times its strength
uniform vec3 uAmbCol;   // sky fill: hemisphere plus ambient
uniform vec4 uShade;    // gains: ambient, sun, rim; and how far fog thins a card
uniform float uOpacity;
varying vec2 vUv;
varying vec3 vSun;
varying vec3 vTint;
varying float vAlpha;
#ifdef CARD_GLOW
uniform vec3 uGlowCol;  // the flash inside the cloud, whatever the sky's light
uniform float uHazeDark; // how much darker than the haze a far thunderhead stays
varying float vGlow;
#endif
#include <fog_pars_fragment>
#ifdef CARD_SRGB_OUT
  // Drawn into the storm's own target for an sRGB canvas (storm.js
  // drawPuffs), where three hands the fog colour over linear (the canvas
  // gets it sRGB): a fogged thunderhead greyed toward the wrong haze, a
  // shade darker than the one the canvas draws, until this.
  #define fogColor sRGBTransferOETF(vec4(fogColor, 1.0)).rgb
#endif

void main() {
  vec4 t = texture2D(uAtlas, vUv);
  float a = t.a * vAlpha * uOpacity;
  if (a < 0.003) discard;
  // The atlas is uploaded premultiplied, so its mipmaps don't grey a puff's
  // rim into the black around it; undo that for the light, which the bake
  // wrote sRGB-encoded (squared back to linear: near enough for light, and
  // cheaper than a pow per pixel of cloud).
  vec3 lt = t.rgb / max(t.a, 0.004);
  lt *= lt;
  // Blend the three baked lights by where the key light sits in the card's
  // frame: left (red), right (blue), overhead (green). A light behind the
  // eye finds every bulge lit by one of them (the brightest), one below or
  // behind the puff only its shaded side (the darkest).
  vec3 s = vSun;
  float wl = max(-s.x, 0.0), wr = max(s.x, 0.0), wt = max(s.y, 0.0);
  float wb = max(-s.y, 0.0), wf = max(s.z, 0.0), wk = max(-s.z, 0.0);
  float bright = max(max(lt.r, lt.g), lt.b);
  float shade = min(min(lt.r, lt.g), lt.b);
  float lit = (wl * lt.r + wr * lt.b + wt * lt.g + wf * bright + (wb + wk) * shade)
    / (wl + wr + wt + wb + wf + wk + 1e-4);
  vec3 col = vTint * (uAmbCol * uShade.x + uSunCol * lit * uShade.y);
  // The sun behind the cloud: a silver lining where the puff runs thin.
  col += uSunCol * (wk * (1.0 - t.a) * (1.0 - t.a) * uShade.z);
  #ifdef CARD_GLOW
    // Lightning lights the puff from within: its own light, not the sky's,
    // so a midnight thunderhead flares as hard as a noon one — still shaped
    // by the puff's baked bulges, so it glows as a cloud, not a lamp.
    col = mix(col, uGlowCol * (0.55 + 0.45 * bright), 0.75 * vGlow);
  #endif
  gl_FragColor = vec4(col, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #ifdef CARD_SRGB_OUT
    // ...and three writes a target linear: encode here, where the canvas
    // draw would, so the puffs blend in the canvas's own bytes and the lay
    // over the frame is plain arithmetic.
    gl_FragColor = sRGBTransferOETF(gl_FragColor);
  #endif
  #ifdef CARD_GLOW
    vec3 unfogged = gl_FragColor.rgb;
  #endif
  #include <fog_fragment>
  #ifdef USE_FOG
    // Far cards thin into the haze as well as taking its colour, so a
    // distant cloud doesn't hang as a fog-coloured cut-out against the sky.
    #ifdef CARD_GLOW
      // …except where lightning lights one: a flash carries through rain
      // and haze that grey the cloud around it, so a stroke three
      // kilometres off still lights its belly for you.
      gl_FragColor.rgb -= fogColor * (fogFactor * uHazeDark * (1.0 - vGlow));
      gl_FragColor.rgb = mix(gl_FragColor.rgb, unfogged, 0.7 * vGlow);
      gl_FragColor.a *= 1.0 - fogFactor * uShade.w * (1.0 - vGlow);
    #else
      gl_FragColor.a *= 1.0 - fogFactor * uShade.w;
    #endif
  #endif
}
`;

// The veil: a full-screen wash in the cloud's light, drawn over the world
// (under the HUD-like markers at 999) while the eye is inside a puff.
const VEIL_VERT = /* glsl */ `
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const VEIL_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
void main() {
  gl_FragColor = vec4(uColor, uAlpha);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * One draw of puff cards: `cards` as cloudCards deals them, lit by the shared
 * light and atlas `uniforms`, at strength `opacity` (material.opacity, which
 * storm.js dims). The cards' coordinates are the mesh's own frame, so a
 * parent's transform carries them (the storm front marches its group); sort
 * takes the eye in that frame. With `glow`, each card also takes a lightning
 * level, written by card index into userData.glow and handed to the GPU by
 * userData.pushGlow() in draw order.
 */
export function cardMesh(cards, { opacity, uniforms, glow = false }) {
  const quad = new THREE.InstancedBufferGeometry();
  quad.setAttribute("corner", new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
  quad.setIndex([0, 1, 2, 0, 2, 3]);
  // `position` only so three knows the vertex count; the cards place
  // themselves in the shader.
  quad.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(12), 3));
  // three orders see-through draws by the middle of each one's bounding
  // sphere; update() moves this one's middle to where the deck should sort.
  quad.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  const n = cards.length;
  const iBase = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4).setUsage(THREE.DynamicDrawUsage);
  const iLook = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4).setUsage(THREE.DynamicDrawUsage);
  const iRect = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4).setUsage(THREE.DynamicDrawUsage);
  const iTint = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3).setUsage(THREE.DynamicDrawUsage);
  quad.setAttribute("iBase", iBase);
  quad.setAttribute("iLook", iLook);
  quad.setAttribute("iRect", iRect);
  quad.setAttribute("iTint", iTint);
  const iGlow = glow ? new THREE.InstancedBufferAttribute(new Float32Array(n), 1).setUsage(THREE.DynamicDrawUsage) : null;
  if (iGlow) quad.setAttribute("iGlow", iGlow);
  quad.instanceCount = n;

  const extra = { uOpacity: { value: opacity } };
  if (glow) {
    extra.uGlowCol = { value: new THREE.Color(1.2, 1.3, 1.65) };
    extra.uHazeDark = { value: 0 };
  }
  const material = new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, extra]),
    defines: glow ? { CARD_GLOW: "" } : {},
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false, // overlapping puffs melt together instead of cutting seams
    fog: true,
  });
  // The shared light and texture uniforms: one object, every deck.
  for (const k of ["uAtlas", "uSunCol", "uAmbCol", "uShade", "uCamRight", "uSunDir", "uNearFade"]) {
    material.uniforms[k] = uniforms[k];
  }
  material.opacity = opacity; // storm.js dims a deck through this
  const mesh = new THREE.Mesh(quad, material);
  mesh.frustumCulled = false; // the cards span the map; their quad sits at the origin
  mesh.visible = false; // until the atlas lands
  mesh.userData.cards = cards;

  // Back to front, so a near puff lays over a far one. Clouds stand still
  // in their frame, so this only reruns when the eye has moved a fair way.
  const order = cards.map((_, i) => i);
  const key = new Float32Array(n);
  const m = [0, 0, 0];
  const glowOf = glow ? new Float32Array(n) : null;
  // A per-card strength on top of the card's own, by card index: the storm
  // front thins the cumulus it swallows (storm.js swallowDecks).
  const fade = new Float32Array(n).fill(1);
  const pushFade = () => {
    const L = iLook.array;
    for (let j = 0; j < n; j++) L[j * 4 + 3] = cards[order[j]].opacity * fade[order[j]];
    iLook.needsUpdate = true;
  };
  const pushGlow = () => {
    const G = iGlow.array;
    for (let j = 0; j < n; j++) G[j] = glowOf[order[j]];
    iGlow.needsUpdate = true;
  };
  let sx = Infinity, sy = 0, sz = 0;
  mesh.userData.sort = (eye, force = false) => {
    if (!force && (eye.x - sx) ** 2 + (eye.y - sy) ** 2 + (eye.z - sz) ** 2 < 25 * 25) return false;
    sx = eye.x; sy = eye.y; sz = eye.z;
    for (let i = 0; i < n; i++) {
      puffMid(cards[i], m);
      key[i] = (m[0] - eye.x) ** 2 + (m[1] - eye.y) ** 2 + (m[2] - eye.z) ** 2;
    }
    order.sort((a, b) => key[b] - key[a]);
    mesh.userData.nearest = Math.sqrt(key[order[n - 1]]);
    const B = iBase.array, L = iLook.array, R = iRect.array, T = iTint.array;
    for (let j = 0; j < n; j++) {
      const c = cards[order[j]];
      B[j * 4] = c.x; B[j * 4 + 1] = c.y; B[j * 4 + 2] = c.z; B[j * 4 + 3] = c.size;
      L[j * 4] = c.cell; L[j * 4 + 1] = c.mirror; L[j * 4 + 2] = c.squash; L[j * 4 + 3] = c.opacity * fade[order[j]];
      R.set(PUFF_RECTS[c.cell], j * 4);
      T[j * 3] = c.r; T[j * 3 + 1] = c.g; T[j * 3 + 2] = c.b;
    }
    iBase.needsUpdate = iLook.needsUpdate = iRect.needsUpdate = iTint.needsUpdate = true;
    if (glow) pushGlow();
    return true;
  };
  mesh.userData.fade = fade;
  mesh.userData.pushFade = pushFade;
  if (glow) {
    mesh.userData.glow = glowOf;
    mesh.userData.pushGlow = pushGlow;
  }
  mesh.userData.sort({ x: 0, y: 0, z: 0 }, true);
  return mesh;
}

/**
 * The light and atlas every card mesh reads: buildClouds fills them each
 * frame from the sky and lands the atlas in uAtlas; a card mesh built on the
 * same object (the storm front's) is lit by the same sun for free.
 */
export function cardUniforms() {
  return {
    uAtlas: { value: null },
    uSunCol: { value: new THREE.Color(1, 1, 1) },
    uAmbCol: { value: new THREE.Color(0.6, 0.7, 0.8) },
    uShade: { value: new THREE.Vector4(...SHADE) },
    uCamRight: { value: new THREE.Vector3(1, 0, 0) },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uNearFade: { value: new THREE.Vector2(...NEAR_FADE) },
  };
}

/**
 * Build both decks into `scene`. `sky` is buildSky's handle: the cards take
 * their light from its sun (or moon), hemisphere and ambient every frame,
 * and `renderer` says whether that light is filmed (tier 2's tone mapping).
 * Returns { low, high, veil, update(camera) } — main.js calls update right
 * before each render; storm.js dims low/high through material.opacity.
 */
export function buildClouds(scene, field, sky, renderer = null) {
  const uniforms = cardUniforms();
  const lowCards = cloudCards(field.seed, field.radius, DECKS.low);
  const highCards = cloudCards(field.seed, field.radius, DECKS.high);
  // The high deck is drawn first: from anywhere a pilot flies, it's the
  // farther of the two (transparent draws of equal depth go in build order).
  const high = cardMesh(highCards, { opacity: DECKS.high.opacity, uniforms });
  const low = cardMesh(lowCards, { opacity: DECKS.low.opacity, uniforms });
  scene.add(high);
  scene.add(low);

  const veil = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color() }, uAlpha: { value: 0 } },
      vertexShader: VEIL_VERT,
      fragmentShader: VEIL_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      fog: false,
    }),
  );
  veil.frustumCulled = false;
  veil.renderOrder = 998;
  veil.visible = false;
  scene.add(veil);

  if (typeof document !== "undefined") {
    new THREE.TextureLoader().load(
      ATLAS_URL.href,
      (tex) => {
        // Premultiplied on upload so the mip chain averages coverage and
        // light together (the shader divides it back out).
        tex.premultiplyAlpha = true;
        tex.colorSpace = THREE.NoColorSpace; // the shader decodes the light itself
        tex.generateMipmaps = true;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.anisotropy = 4;
        tex.needsUpdate = true;
        uniforms.uAtlas.value = tex;
        low.visible = high.visible = true;
      },
      undefined,
      (err) => console.warn("cloud atlas unavailable — the sky stays clear:", err?.message ?? err),
    );
  }

  const eye = new THREE.Vector3();
  const fwd = new THREE.Vector3();
  const fill = new THREE.Color();
  const lowOp0 = DECKS.low.opacity;
  return {
    low,
    high,
    veil,
    /** Every card's puff middle, [[x, y, z], …] (probes and the director). */
    centres(deck = "low") {
      const m = [0, 0, 0];
      return (deck === "high" ? highCards : lowCards).map((c) => puffMid(c, m).slice());
    },
    update(camera) {
      const { sun, hemi, ambient } = sky.parts;
      const lift = renderer && renderer.toneMapping !== THREE.NoToneMapping ? TONE_LIFT : 1;
      uniforms.uSunDir.value.copy(sun.position).sub(sun.target.position).normalize();
      uniforms.uSunCol.value.copy(sun.color).multiplyScalar(sun.intensity * lift * (1 + DUSK_GLOW * (sky.duskK ?? 0)));
      uniforms.uAmbCol.value
        .copy(hemi.color)
        .multiplyScalar(hemi.intensity * lift)
        .add(fill.copy(ambient.color).multiplyScalar(ambient.intensity * lift));
      cameraRight(camera, uniforms.uCamRight.value);
      for (const d of [low, high]) d.material.uniforms.uOpacity.value = d.material.opacity;
      camera.getWorldPosition(eye);
      if (!low.visible) {
        veil.visible = false; // no deck (atlas pending, or hidden): no veil
        return;
      }
      low.userData.sort(eye);
      high.userData.sort(eye);
      // Where the decks sort among the other see-through draws: straight
      // ahead, as far off as the nearest cloud — so a smoke trail or a
      // tracer nearer than any cloud lays over the clouds and one farther
      // than all of them lies under — but short of the sea's own sort point
      // (the map's centre), since a cloud always hangs above the water. The
      // backdrop (stars, the sun's glow, the sea) goes first regardless
      // (world.js buildSky).
      camera.getWorldDirection(fwd);
      let at = Math.min(low.userData.nearest, high.userData.nearest);
      const sea = -(eye.x * fwd.x + eye.y * fwd.y + eye.z * fwd.z);
      if (sea > 0) at = Math.min(at, sea);
      at = THREE.MathUtils.clamp(at * 0.9, 1, 1500);
      low.geometry.boundingSphere.center.copy(eye).addScaledVector(fwd, at);
      high.geometry.boundingSphere.center.copy(low.geometry.boundingSphere.center);
      // The veil: as thick as the eye is deep in cloud, in the cloud's own
      // light, and as dim as a storm has made the deck.
      const depth = eye.y < 350 || eye.y > 3000 ? 0 : Math.max(cloudDepth(lowCards, eye.x, eye.y, eye.z), 0.6 * cloudDepth(highCards, eye.x, eye.y, eye.z));
      const k = THREE.MathUtils.smoothstep(depth, 0, 0.6) * VEIL_MAX * (low.material.opacity / lowOp0);
      veil.visible = k > 0.002;
      if (veil.visible) {
        const vm = veil.material.uniforms;
        vm.uAlpha.value = k;
        vm.uColor.value
          .copy(uniforms.uAmbCol.value)
          .multiplyScalar(SHADE[0])
          .add(fill.copy(uniforms.uSunCol.value).multiplyScalar(SHADE[1] * VEIL_LIT));
      }
    },
    /** The decks' cards (tests and probes). */
    cards: { low: lowCards, high: highCards },
    uniforms,
  };
}
